import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from '../../environments/environment';
import {IAuthData} from '../models/IAuthData';
import {Store} from '@ngrx/store';
import {IAppState} from '../models/IAppState';
import {authorize, getUserData, setLocalUser} from '../store/actions/user.actions';
import {saveToLocalStorage} from '../store/actions/store.actions';
import {Router} from '@angular/router';
import {PushNotificationService} from './push-notification.service';
import {
  CordovaIFrameNavigator,
  Profile,
  User,
  UserManager,
  UserManagerSettings,
  WebStorageStateStore
} from 'oidc-client';
import {AppConfigService} from './appconfig.service';
import {IIdentityServerConfig} from '../models/IConfiguration';
import {IRegistrationRequest} from '../models/IRegistrationRequest';
import {IResponse} from '../models/IResponse';
import {LoadingController, Platform} from '@ionic/angular';
import {IVerifyAndLoginUser} from '../models/IVerifyAndLoginUser';
import {ILoginWithDeviceUuid} from '../models/ILoginWithDeviceUuid';
import {IVerificationCodeGenerationRequest} from '../models/IVerificationCodeGenerationRequest';
import {take} from 'rxjs/operators';
import {ILocalUser} from '../models/ILocalUser';
import {IIosDeviceVerificationRequest} from '../models/IIosDeviceVerificationRequest';
import {IIosDeviceVerificationResponse} from '../models/IIosDeviceVerificationResponse';
import {ISmsInviteSetting} from './location.service';
import {CapacitorPopupNavigator} from '../utils/capacitor-popup-navigator';


@Injectable({
  providedIn: 'root'
})
export class AuthService {
  public user: User;
  public claims: Profile;
  public authData$: Observable<IAuthData> = this.store.select(
    (state) => state.AppStateReducer.authData
  );
  public locationUser$: Observable<User> = this.store.select(
    (state) => state.AppStateReducer.locationUser
  );
  public localUser$: Observable<ILocalUser> = this.store.select(
    (state) => state.AppStateReducer.localUser
  );
  private accessToken: string;
  private manager: UserManager;
  private idToken: string;
  private readonly loadingController = inject(LoadingController);
  private authReturnLoading?: HTMLIonLoadingElement;
  private authReturnLoadingDismissTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private http: HttpClient,
    private router: Router,
    private pushNotificationService: PushNotificationService,
    private store: Store<{AppStateReducer: IAppState}>,
    private appConfig: AppConfigService,
    private platform: Platform
  ) {
    this.authData$.subscribe((authData: IAuthData) => {
      if (authData) {
        this.accessToken = authData.accessToken;
        this.pushNotificationService.registerPushService();
      }
    });

    this.locationUser$.subscribe((user: User) => {
      if (user && user.access_token) {
        this.pushNotificationService.registerPushService();
        this.idToken = user.id_token;
      }
    });

    this.setUserData();
  }

  public async logout() {
    const showNativeLogoutLoading = !!this.idToken
      && this.platform.is('hybrid')
      && (this.platform.is('android') || this.platform.is('ios'));

    if (showNativeLogoutLoading) {
      await this.presentAuthReturnLoading();
    }

    try {
      this.store.dispatch(authorize({isAuthorised: false, authData: null}));
      this.store.dispatch(saveToLocalStorage());
      localStorage.removeItem('cometChatToken');
      await this.clear(true);
    } catch (error) {
      await this.hideAuthReturnLoading();
      throw error;
    }
  }

  public async hideAuthReturnLoading(): Promise<void> {
    this.clearAuthReturnLoadingDismissTimer();
    const loading = this.authReturnLoading;
    if (!loading) {
      return;
    }

    this.authReturnLoading = undefined;
    try {
      await loading.dismiss();
    } catch (_) {
      // The maximum duration may have dismissed it already.
    }
  }

  public getToken(): string {
    if (!!this.user) {
      return this.user.access_token;
    }
    return this.accessToken;
  }

  public getHeader(): HttpHeaders {
    if (this.getToken()) {
      return new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + this.getToken()
      });
    } else {
      return new HttpHeaders({'Content-Type': 'application/json'});
    }
  }

  public registerUserBySmsLink(checkinId: string, instantChatId: string, pin?: string, isExtendedUrl?: boolean): Observable<IAuthData> {
    const params: any = { };
    if (pin) {
      params.pin = pin;
    }
    if (isExtendedUrl) {
      params.isExtendedUrl = isExtendedUrl;
    }
    const headers = {
      'errors': 'silent'
    };

    return this.http.get<IAuthData>(`${environment.apiUrl}/checkins/${checkinId}/chat/instant/${instantChatId}/login-info`,
      { params, headers }
    );
  }

  public inviteUserBySmsLink(checkinId: string, settings: ISmsInviteSetting): Observable<IAuthData> {
    return this.http.post<IAuthData>(`${environment.apiUrl}/checkins/${checkinId}/chat/instant`, settings);
  }

  public registerUserByPhoneNumber(phoneNumber: string, appHash: string = ''): Observable<any> {
    const headers = {
      'content-type': 'application/json',
      'errors': 'silent'
    };
    return this.http.post<any>(`${environment.loginUrl}/user/registerUserByPhoneNumber/${phoneNumber}/${appHash}`, {}, {'headers': headers});
  }

  public registerUserByPhoneNumberViaFcm(phoneNumber: string, recaptchaToken: string = '', iosReceipt: string = ''): Observable<any> {
    const request: IVerificationCodeGenerationRequest = {
      phoneNumber: phoneNumber,
      recaptchaToken: recaptchaToken,
      iosReceipt: iosReceipt
    };
    const headers = {
      'content-type': 'application/json',
      'errors': 'silent'
    };
    return this.http.post<any>(`${environment.loginUrl}/user/registerUserByPhoneNumber`, request,
      {'headers': headers});
  }

  public generateVerificationCode(phoneNumber: string, appHash: string = ''): Observable<any> {

    return this.http.get<any>(`${environment.loginUrl}/user/generateAndSendVerificationCode/${phoneNumber}/${appHash}`);
  }

  public generateAndSendVerificationCode(phoneNumber: string, recaptchaToken: string = '', iosReceipt: string = ''): Observable<any> {
    const request: IVerificationCodeGenerationRequest = {
      phoneNumber: phoneNumber,
      recaptchaToken: recaptchaToken,
      iosReceipt: iosReceipt
    };
    const headers = {
      'content-type': 'application/json',
      'errors': 'silent'
    };
    return this.http.post<any>(`${environment.loginUrl}/user/generateAndSendVerificationCode`, request,
      {'headers': headers});
  }

  public verifyUserRegisteredByPhoneNumber(phoneNumber: string, verificationCode: string): Observable<IAuthData> {
    const headers = new HttpHeaders().set('Content-Type', 'text/plain; charset=utf-8');
    const request: IVerifyAndLoginUser = {
      phoneNumber: phoneNumber,
      verificationCode: verificationCode,
      clientId: environment.authClientId
    };
    return this.http.post<any>(`${environment.loginUrl}/user/verifyUserRegisteredByPhoneNumber`,
      request,
      {headers, responseType: 'text' as 'json'});
  }

  public verifyUserRegisteredByPhoneNumberViaFcm(phoneNumber: string, verificationCode: string): Observable<IAuthData> {
    const headers = new HttpHeaders().set('Content-Type', 'text/plain; charset=utf-8');
    const request: IVerifyAndLoginUser = {
      phoneNumber: phoneNumber,
      verificationCode: verificationCode,
      clientId: environment.authClientId
    };
    return this.http.post<IAuthData>(`${environment.loginUrl}/user/verifyUserRegisteredByPhoneNumberWithFcm`,
      request,
      {headers, responseType: 'text' as 'json'});
  }

  public loginWithPhoneNumber(phoneNumber: string, verificationCode: string): Observable<IAuthData> {
    const request: IVerifyAndLoginUser = {
      phoneNumber: phoneNumber,
      verificationCode: verificationCode,
      clientId: environment.authClientId,
      silent: true
    };
    return this.http.post<IAuthData>(`${environment.loginUrl}/user/loginWithPhoneNumber`, request);
  }

  public loginWithPhoneNumberViaFcm(phoneNumber: string, verificationCode: string): Observable<IAuthData> {
    const request: IVerifyAndLoginUser = {
      phoneNumber: phoneNumber,
      verificationCode: verificationCode,
      clientId: environment.authClientId
    };
    return this.http.post<IAuthData>(`${environment.loginUrl}/user/loginByPhoneNumber`, request);
  }

  getClaims(): Profile {
    return this.user.profile;
  }

  getAuthorizationHeaderValue(): string {
    return `${this.user.token_type} ${this.user.access_token}`;
  }

  startAuthentication(): Promise<void> {
    return this.manager.signinRedirect();
  }

  async completeAuthentication(): Promise<User> {
    this.user = await this.manager.signinRedirectCallback();
    this.completeProcess();
    return this.user;
  }

  async signInPopup(): Promise<User> {
    console.log('signInPopup', this);
    this.user = await this.manager.signinPopup();
    this.completeProcess();
    return this.user;
  }

  async signInPopUpCallback(): Promise<User> {
    this.user = await this.manager.signinPopupCallback();
    this.completeProcess();
    return this.user;
  }

  logOutAsLocationUser() {
    return this.clear(true);
  }

  async clear(reload = true): Promise<void> {
    localStorage.removeItem('cometChatToken');
    localStorage.removeItem('unauthUrl');
    localStorage.removeItem('checkinFormSettings');
    localStorage.removeItem('AppState');
    this.manager.removeUser();
    this.user = null;
    if (this.idToken) {
      if (this.platform.is('hybrid') && (this.platform.is('android') || this.platform.is('ios'))) {
        await this.manager.signoutPopup({'id_token_hint': this.idToken});
        window.location.pathname = '/';
      } else {
        await this.manager.signoutRedirect({'id_token_hint': this.idToken});
      }
    } else if (reload) {
      await new Promise((res) => setTimeout(() => res(null), 1));
      window.location.pathname = '/';
    }
  }

  getUser(): User {
    return this.user;
  }

  public loginWithPhoneNumberAndDeviceUuid(phoneNumber: string, deviceUuid: string): Observable<IAuthData> {
    const request: ILoginWithDeviceUuid = {
      phoneNumber: phoneNumber,
      deviceUuid: deviceUuid,
      clientId: environment.authClientId
    };
    return this.http.post<IAuthData>(`${environment.loginUrl}/user/loginWithPhoneNumberAndDeviceUuid`, request);
  }

  public saveRegistrationRequest(request: IRegistrationRequest): Observable<IResponse> {
    return this.http.post<IResponse>(`${environment.loginUrl}/user/saveRegistrationRequest`, request);
  }

  public isRegistrationRequestConfirmed(phoneNumber: string, deviceUuid: string): Observable<boolean> {
    const params = `${phoneNumber}/${deviceUuid}`;
    return this.http.get<boolean>(`${environment.loginUrl}/user/isRegistrationRequestConfirmed/${params}`);
  }

  public verifyIosClient(request: IIosDeviceVerificationRequest): Observable<IIosDeviceVerificationResponse> {
    return this.http.post<IIosDeviceVerificationResponse>(
      `${environment.loginUrl}/deviceVerification/verifyIosClient`,
      request,
      {
        headers: {
          errors: 'silent'
        },
        params: {
          silent: true
        }
      }
    );
  }

  public async saveVisitorProfile() {
    const data = await this.localUser$.pipe(take(1)).toPromise();
    localStorage[`visitorProfile`] = JSON.stringify(data);
  }

  public getSavedVisitorProfile(): ILocalUser {
    if (localStorage[`visitorProfile`]) {
      try {
        const profile = JSON.parse(localStorage[`visitorProfile`]);
        console.log('profile', profile);
        return profile;
      } catch (e) {
        return null;
      }
    }

    return null;
  }

  public restoreVisitorProfile() {
    const profile = this.getSavedVisitorProfile();
    if (profile) {
      this.store.dispatch(setLocalUser({localUser: profile}));
      this.store.dispatch(saveToLocalStorage());
    }
    localStorage.removeItem('visitorProfile');
  }

  public getLoginCookie(userName: string): Observable<any> {
    return this.http.get<any>(`${environment.loginUrl}/account/getLoginCookie/${userName}`, {withCredentials: true});
  }

  private async setUserData() {
    const config = this.appConfig.config.identityConfig;
    this.manager = new UserManager(this.getClientSettings(config));
    this.user = await this.manager.getUser();

    if (!!this.user) {
      this.claims = this.getClaims();
      this.store.dispatch(getUserData({} as any));
    }
  }

  private getClientSettings(config: IIdentityServerConfig): UserManagerSettings {
    if (this.platform.is('hybrid') && (this.platform.is('android') || this.platform.is('ios'))) {
      return {
        authority: config.identityEndpoint,
        client_id: config.clientId,
        redirect_uri: `${config.deviceHostUrl}auth_callback`,
        post_logout_redirect_uri: `${config.deviceHostUrl}`,
        silent_redirect_uri: `${config.deviceHostUrl}renew-callback.html`,
        response_type: config.responseType,
        scope: config.requiredScopes,
        filterProtocolClaims: true,
        loadUserInfo: true,
        automaticSilentRenew: true,
        userStore: new WebStorageStateStore({store: localStorage}),
        iframeNavigator: new CordovaIFrameNavigator(),
        popupNavigator: new CapacitorPopupNavigator(() => this.showAuthReturnLoading()),
        popupWindowFeatures: 'location=yes,toolbar=yes,width=500,height=500,left=100,top=100',
        popupWindowTarget: '_system'
      };
    } else {
      const hostUrl = (location.host === 'localhost:8100') ? 'http://localhost:8100/' : config.hostUrl;

      return {
        authority: config.identityEndpoint,
        client_id: config.clientId,
        redirect_uri: `${hostUrl}auth_callback`,
        post_logout_redirect_uri: `${hostUrl}`,
        silent_redirect_uri: `${hostUrl}renew-callback.html`,
        response_type: config.responseType,
        scope: config.requiredScopes,
        filterProtocolClaims: true,
        loadUserInfo: true,
        automaticSilentRenew: true,
        userStore: new WebStorageStateStore({store: localStorage})
      };
    }
  }

  private async showAuthReturnLoading(): Promise<void> {
    await this.presentAuthReturnLoading();
    this.clearAuthReturnLoadingDismissTimer();
    this.authReturnLoadingDismissTimer = setTimeout(
      () => void this.hideAuthReturnLoading(),
      4000
    );
  }

  private async presentAuthReturnLoading(): Promise<void> {
    if (this.authReturnLoading) {
      return;
    }

    const loading = await this.loadingController.create({
      cssClass: 'auth-return-loading',
      showBackdrop: true
    });
    this.authReturnLoading = loading;
    void loading.onDidDismiss().then(() => {
      if (this.authReturnLoading === loading) {
        this.authReturnLoading = undefined;
      }
    });
    await loading.present();
  }

  private clearAuthReturnLoadingDismissTimer(): void {
    if (this.authReturnLoadingDismissTimer !== undefined) {
      clearTimeout(this.authReturnLoadingDismissTimer);
      this.authReturnLoadingDismissTimer = undefined;
    }
  }

  private completeProcess(): void {
    this.claims = this.getClaims();
    this.idToken = this.user.id_token;
  }
}
